import { useState, useEffect, useRef } from "react";
import PortalConfetti from "./PortalConfetti";
import { doc, setDoc, getDoc, increment } from "firebase/firestore";
import { db } from "../firebase";
import { LOCATIONS_MAP } from "../maps";
import { LOCATIONS } from "../data";
import { getDailyItem, updateGameStats } from "../utils/gameLogic";
import { usePersistentStorage, useKeyboardNavigation } from "../hooks/useGameHooks";

/**
 * Location guessing mode — 5 guesses with an image carousel that reveals more clues
 * on each wrong guess. Progressive hint reveals the nation after 3 wrong guesses.
 *
 * @param {{ version: string, targetDateObj: Date, targetDateStr: string, isArchive: boolean, setIsModalOpen: (v: boolean) => void, guessedLocations: Array<Object>, setGuessedLocations: (g: Array<Object>) => void }} props
 * @param {string} props.version - "old" or "new".
 * @param {Date} props.targetDateObj - Target date object.
 * @param {string} props.targetDateStr - Date string for localStorage keys.
 * @param {boolean} props.isArchive - Whether viewing an archived date.
 * @param {(v: boolean) => void} props.setIsModalOpen - Opens global stats modal.
 * @param {Array<Object>} props.guessedLocations - Previously submitted location guesses.
 * @param {(g: Array<Object>) => void} props.setGuessedLocations - Guess state setter.
 * @returns {JSX.Element} The location game UI.
 */
export default function LocationMode({
  version,
  targetDateObj,
  targetDateStr,
  isArchive,
  setIsModalOpen,
  guessedLocations,
  setGuessedLocations,
}) {
  const [currentGuess, setCurrentGuess] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [collection, setCollection] = usePersistentStorage(
    "kisekidle-collection-locations",
    []
  );
  const [isCopied, setIsCopied] = useState(false);

  const [targetLocation] = useState(
    getDailyItem(LOCATIONS, targetDateObj, LOCATIONS_MAP)
  );

  const MAX_GUESSES = 5;
  const wrongGuessesCount = guessedLocations.filter(
    (loc) => loc.id !== targetLocation.id
  ).length;
  const hasWon = guessedLocations.some((loc) => loc.id === targetLocation.id);
  const guessesLeft = MAX_GUESSES - guessedLocations.length;
  const isGameOver = guessesLeft <= 0 && !hasWon;

  /** Processes a location guess — checks win, updates stats, adds to collection. */
  const handleGuess = (selectedLoc) => {
    if (hasWon || isGameOver) return;
    if (guessedLocations.find((l) => l.id === selectedLoc.id)) return;

    const newGuesses = [...guessedLocations, selectedLoc];
    const isWin = selectedLoc.id === targetLocation.id;
    const isLoss = newGuesses.length >= MAX_GUESSES && !isWin;

    if (!isArchive && (isWin || isLoss)) {
      updateGameStats("location", isWin, newGuesses.length, MAX_GUESSES);
    }

    if (isWin) {
      const selectedId = String(selectedLoc.id);
      if (!collection.some((id) => String(id) === selectedId)) {
        setCollection([...collection, selectedId]);
      }
    }

    setGuessedLocations(newGuesses);
    setCurrentGuess("");
    setSuggestions([]);
  };

  const { focusedIndex, setFocusedIndex, handleKeyDown, listRef } =
    useKeyboardNavigation(suggestions, handleGuess);

  // --- ANTI-CHEAT WIN FLAG ---
  useEffect(() => {
    if (hasWon) {
      const storageKey = `kisekidle-locations-${targetDateStr}`;
      const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      storedData.hasWon = true;
      localStorage.setItem(storageKey, JSON.stringify(storedData));
    }
  }, [hasWon, targetDateStr]);

  const [isNationRevealed, setIsNationRevealed] = useState(false);
  const guessesNeededForNation = Math.max(0, 3 - wrongGuessesCount);

  const [globalStats, setGlobalStats] = useState(null);

  // --- CAROUSEL LOGIC ---
  const maxRevealedIndex =
    hasWon || isGameOver
      ? targetLocation.imageFilePaths.length - 1
      : Math.min(wrongGuessesCount, targetLocation.imageFilePaths.length - 1);

  const [currentImageIndex, setCurrentImageIndex] = useState(maxRevealedIndex);

  useEffect(() => {
    if (!hasWon && !isGameOver) {
      setCurrentImageIndex(maxRevealedIndex);
    }
  }, [maxRevealedIndex, hasWon, isGameOver]);

  const currentImageUrl = targetLocation.imageFilePaths[currentImageIndex];

  /** Filters locations by name, display title, or nation, excluding already-guessed ones. */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setCurrentGuess(value);
    setFocusedIndex(-1);
    if (value.length > 0) {
      const query = value.toLowerCase().trim();
      const filteredLocs = LOCATIONS.filter((loc) => {
        const matchName = (loc.locationName || "")
          .toLowerCase()
          .includes(query);
        const matchDisplay = (loc.displayTitle || "")
          .toLowerCase()
          .includes(query);
        const matchNation = (loc.locationNation || "")
          .toLowerCase()
          .includes(query);
        return (matchName || matchDisplay || matchNation) &&
          !guessedLocations.some((g) => g.id === loc.id);
      }).sort((a, b) => {
        const nameA = (a.locationName || "").toLowerCase();
        const displayA = (a.displayTitle || "").toLowerCase();
        const nationA = (a.locationNation || "").toLowerCase();

        const nameB = (b.locationName || "").toLowerCase();
        const displayB = (b.displayTitle || "").toLowerCase();
        const nationB = (b.locationNation || "").toLowerCase();

        const aNameStart =
          nameA.startsWith(query) || displayA.startsWith(query);
        const bNameStart =
          nameB.startsWith(query) || displayB.startsWith(query);

        if (aNameStart && !bNameStart) return -1;
        if (!aNameStart && bNameStart) return 1;

        const aNationStarts = nationA.startsWith(query);
        const bNationStarts = nationB.startsWith(query);

        if (aNationStarts && !bNationStarts) return -1;
        if (!aNationStarts && bNationStarts) return 1;

        return nameA.localeCompare(nameB);
      });
      setSuggestions(filteredLocs.slice(0, 15));
    } else {
      setSuggestions([]);
    }
  };

  const syncInProgress = useRef(false);

  // --- LOCATION MODE TRACKING ---
  useEffect(() => {
    const syncReceipt = `synced-location-${targetDateStr}`;
    const hasAlreadySynced = localStorage.getItem(syncReceipt);

    if ((hasWon || isGameOver) && !hasAlreadySynced && !syncInProgress.current) {
      const updateAndFetchStats = async () => {
        syncInProgress.current = true;
        try {
          const statsRef = doc(db, "location_stats", targetDateStr);
          const updateData = {
            totalPlays: increment(1),
            totalWins: hasWon ? increment(1) : increment(0),
            totalWinningAttempts: hasWon
              ? increment(guessedLocations.length)
              : increment(0),
            guessTally: {},
          };
          guessedLocations.forEach((loc) => {
            if (loc && loc.locationName)
              updateData.guessTally[loc.locationName] = increment(1);
          });
          await setDoc(statsRef, updateData, { merge: true });
          localStorage.setItem(syncReceipt, "true");
          const updatedDoc = await getDoc(statsRef);
          if (updatedDoc.exists()) setGlobalStats(updatedDoc.data());
        } catch (error) {
          console.error("Firebase Error:", error);
        } finally {
          syncInProgress.current = false;
        }
      };
      updateAndFetchStats();
    }
  }, [hasWon, isGameOver, guessedLocations.length, targetDateStr]);

  /** Copies location-mode results (emoji or text format) to clipboard. */
  const handleShare = (format) => {
    const score = hasWon ? guessedLocations.length : "X";
    let shareText = `Kisekidle - Location (${targetDateStr})\nGuesses: ${score}/${MAX_GUESSES}\n\n`;
    
    if (format === "emoji") {
      const emojis = guessedLocations
        .map((loc) => (loc.id === targetLocation.id ? "🟩" : "🟥"))
        .join("");
      shareText += emojis;
    } else {
      guessedLocations.forEach((loc, i) => {
        shareText += `${i + 1}. ${loc.locationName} ${loc.id === targetLocation.id ? "✅" : "❌"}\n`;
      });
    }

    shareText += `\n\nhttps://kisekidle.app/`;

    navigator.clipboard.writeText(shareText).then(() => {
      setIsCopied(format);
      setTimeout(() => setIsCopied(null), 2000);
    });
  };

  return (
    <div className="game-terminal location-terminal">
      <div className="header-container">
        <h2 className="mode-title">Guess the Location</h2>
        <div className="guesses-remaining">
          Guesses Remaining: {guessesLeft}
        </div>
        <p className="clue-info">
          🔍 Clue <span className="clue-current">{maxRevealedIndex + 1}</span> of <span className="clue-total">{targetLocation.imageFilePaths.length}</span> Unlocked
        </p>
      </div>

      <div className="player-panel location-image-panel">
        <img
          src={currentImageUrl}
          alt={`Location Clue ${currentImageIndex + 1}`}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>

      <div className="clue-dots-container">
        {[0, 1, 2, 3, 4].map((index) => {
          const isUnlocked = index <= maxRevealedIndex;
          const isActive = currentImageIndex === index;

          return (
            <button
              key={index}
              onClick={() => isUnlocked && setCurrentImageIndex(index)}
              disabled={!isUnlocked}
              className={`clue-dot-btn ${isActive ? "active" : ""} ${isUnlocked ? "unlocked" : "locked"}`}
            >
              {isActive ? "📍" : index + 1}
            </button>
          );
        })}
      </div>

      {hasWon && (
        <div className="victory-box">
          <PortalConfetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={400}
            gravity={0.15}
          />
          <h3>🎉 You got it! 🎉</h3>
          <p>
            The location was <strong>{targetLocation.locationName}</strong>.
          </p>
          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}

      {isGameOver && (
        <div className="game-over-box">
          <h3>❌ Out of guesses! ❌</h3>
          <p>
            The correct location was{" "}
            <strong>{targetLocation.locationName}</strong>.
          </p>
          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}

      <div className="hints-container">
        <div className="hint-wrapper">
          <button
            className={`hint-btn ${isNationRevealed || hasWon || isGameOver
                ? "revealed"
                : guessesNeededForNation <= 0
                  ? "unlocked"
                  : "locked"
              }`}
            onClick={() =>
              guessesNeededForNation <= 0 && setIsNationRevealed(true)
            }
            disabled={
              guessesNeededForNation > 0 ||
              isNationRevealed ||
              hasWon ||
              isGameOver
            }
          >
            {isNationRevealed || hasWon || isGameOver
              ? `Nation: ${targetLocation.locationNation}`
              : "Reveal Nation"}
          </button>

          {!(hasWon || isGameOver) && (
            <div className="hint-condition">
              {guessesNeededForNation > 0
                ? `Available after ${guessesNeededForNation} more guess${guessesNeededForNation > 1 ? "es" : ""
                }`
                : "Unlocked!"}
            </div>
          )}
        </div>
      </div>

      {!hasWon && !isGameOver && (
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            value={currentGuess}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Guess the location (or search by Nation)..."
            autoFocus
          />
          <button
            className="submit-button"
            onClick={() =>
              suggestions.length > 0 && handleGuess(suggestions[0])
            }
          >
            Submit Guess
          </button>
          {suggestions.length > 0 && (
            <ul className="suggestions-list" ref={listRef}>
              {suggestions.map((loc, index) => (
                <li
                  key={loc.id}
                  className={`${index === focusedIndex ? "focused" : ""} suggestion-item`}
                  onClick={() => handleGuess(loc)}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  {loc.locationName}{" "}
                  <span className="suggestion-subtitle">
                    ({loc.locationNation})
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ul className="guess-list">
        {guessedLocations
          .slice()
          .reverse()
          .map((loc, index) => {
            const isCorrect = loc.id === targetLocation.id;
            return (
              <li
                key={index}
                className={`guess-item ${isCorrect ? "correct" : "incorrect"}`}
              >
                <strong>{loc.locationName}</strong>
                <span>{isCorrect ? "🎉 Correct!" : "❌ Incorrect"}</span>
              </li>
            );
          })}
      </ul>
    </div>
  );
}